import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import type { FolderCountsResponse } from "@/lib/hub-folder/services";

import { useFolderCountsPollInterval } from "@/dashboard/features/hub/realtime/hub-realtime-status";
import { fetchJson } from "@/dashboard/hooks/api/fetch-json";
import { useRequiredAuth } from "@/dashboard/hooks/auth/use-required-auth";
import { canInScope } from "@/dashboard/lib/auth/role-helpers";

export type FolderCounts = FolderCountsResponse;

export const folderCountsQueryKey = (activeTenantId: string) =>
  ["folders", "counts", activeTenantId] as const;

export function useFolderCountsQuery() {
  const { user, activeTenantId } = useRequiredAuth();
  const refetchInterval = useFolderCountsPollInterval();

  return useQuery({
    queryKey: folderCountsQueryKey(activeTenantId ?? ""),
    queryFn: ({ signal }) =>
      fetchJson<FolderCountsResponse>("/api/dashboard/hubFolder/counts", {
        signal,
        tenantId: activeTenantId,
      }),
    enabled: !!activeTenantId && canInScope(user, activeTenantId, "inbox.view"),
    refetchInterval,
    staleTime: 15_000,
    // A background tab may miss events while its interval is suspended.
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/** The headline keeps the existing sum of folder memberships and the Other bucket. */
export function useFlaggedTotalCount(): number | undefined {
  const { data: folderCountsByTenant } = useFolderCountsQuery();

  return useMemo(() => {
    if (!folderCountsByTenant) {
      return undefined;
    }
    let total = 0;
    for (const tenantData of Object.values(folderCountsByTenant)) {
      total += tenantData.otherCount;
      for (const count of Object.values(tenantData.counts)) {
        total += count;
      }
    }
    return total;
  }, [folderCountsByTenant]);
}
